import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { DollarSign, Search, CheckCircle, Clock, XCircle, CreditCard } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Payments = ({ userId }) => {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentMethod, setPaymentMethod] = useState('Not Set');

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API}/payments/user/${userId}`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchPaymentMethod = async () => {
    try {
      const response = await axios.get(`${API}/settings/${userId}`);
      if (response.data?.paymentMethod) {
        setPaymentMethod(response.data.paymentMethod);
      }
    } catch (error) {
      console.error('Error fetching payment method:', error);
    }
  };

  const filterPayments = () => {
    let filtered = payments;

    if (searchTerm) {
      filtered = filtered.filter(payment =>
        payment.paymentId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(payment => payment.status === statusFilter);
    }

    setFilteredPayments(filtered);
  };

  useEffect(() => {
    if (userId) {
      fetchPayments();
      fetchPaymentMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    filterPayments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments, searchTerm, statusFilter]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Successful':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'Pending':
        return <Clock className="w-5 h-5 text-yellow-400" />;
      case 'Not Issued':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-slate-500" />;
    }
  };

  const getStatusGradient = (status) => {
    switch (status) {
      case 'Successful':
        return 'from-green-500 to-emerald-500';
      case 'Pending':
        return 'from-yellow-500 to-blue-500';
      case 'Not Issued':
        return 'from-red-500 to-rose-500';
      default:
        return 'from-gray-500 to-gray-600';
    }
  };

  const totalEarnings = payments
    .filter(p => p.status === 'Successful')
    .reduce((sum, p) => sum + (p.amount || 0), 0);

  const pendingPayments = payments.filter(p => p.status === 'Pending').length;
  const successfulPayments = payments.filter(p => p.status === 'Successful').length;

  return (
    <div className="space-y-6">
      {/* Summary Blocks */}
      <div className="grid [&>*]:min-w-0 grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
            <div className="text-2xl md:text-3xl font-bold text-blue-600">
              ₹{totalEarnings}
            </div>
            <div className="text-xs text-slate-600 mt-1">Total Earnings</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-blue-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <CheckCircle className="w-8 h-8 text-blue-400" />
            </div>
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {successfulPayments}
            </div>
            <div className="text-xs text-slate-600 mt-1">Cleared Payments</div>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-lg border-slate-200 group overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-yellow-500/10 to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <CardContent className="pt-6 relative">
            <div className="flex items-center justify-between mb-2">
              <Clock className="w-8 h-8 text-yellow-400" />
            </div>
            <div className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-yellow-400 to-blue-500 bg-clip-text text-transparent">
              {pendingPayments}
            </div>
            <div className="text-xs text-slate-600 mt-1">Pending Payments</div>
          </CardContent>
        </Card>
      </div>

      {/* Payment Method Display */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardHeader>
          <CardTitle className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Payment Method</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-3 p-4 bg-white rounded-lg border border-slate-200">
            <CreditCard className="w-6 h-6 text-blue-600" />
            <div>
              <p className="text-sm text-slate-500">Current Method</p>
              <p className="text-lg font-semibold text-slate-800">{paymentMethod}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filters */}
      <Card className="bg-white shadow-lg border-slate-200">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by Payment ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white border-slate-200 focus:border-blue-500 text-slate-800"
                />
              </div>
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full md:w-48 h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="all">All Status</option>
              <option value="Pending">Pending</option>
              <option value="Not Issued">Not Issued</option>
              <option value="Successful">Successful</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Payment Cards */}
      <div className="space-y-4">
        {filteredPayments.length === 0 ? (
          <Card className="bg-white shadow-lg border-slate-200">
            <CardContent className="py-12 text-center">
              <DollarSign className="w-16 h-16 mx-auto mb-4 text-slate-600 animate-pulse" />
              <p className="text-slate-500 text-lg mb-2">No payments found</p>
              <p className="text-slate-400 text-sm">Payment records will appear here</p>
            </CardContent>
          </Card>
        ) : (
          filteredPayments.map((payment) => (
            <Card key={payment._id} className="bg-white shadow-lg border-slate-200 hover:border-white/30 transition-all hover:shadow-lg group overflow-hidden relative">
              <div className={`pointer-events-none absolute inset-0 bg-gradient-to-r ${getStatusGradient(payment.status)} opacity-0 group-hover:opacity-5 transition-opacity`} />
              <CardContent className="pt-6 relative">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-3">
                      {getStatusIcon(payment.status)}
                      <h4 className="font-semibold text-lg text-slate-800">{payment.paymentId}</h4>
                    </div>
                    <div className="grid [&>*]:min-w-0 grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500 mb-1">Method</p>
                        <p className="text-slate-800 font-medium">{payment.method || 'UPI'}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 mb-1">Amount</p>
                        <p className="text-green-600 font-bold">₹{payment.amount || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500 mb-1">Date & Time</p>
                        <p className="text-slate-800">{new Date(payment.createdAt).toLocaleString()}</p>
                      </div>
                      <div className="flex items-start">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStatusGradient(payment.status)} text-slate-800`}>
                          {payment.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default Payments;